import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    Tag,
    notification,
    Row,
    Col,
    Statistic,
    Avatar,
    Grid,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    SearchOutlined,
    ShoppingCartOutlined,
    EyeOutlined,
    DollarCircleOutlined
} from '@ant-design/icons';
import { purchaseService } from '../../services/purchaseService';
import PurchaseForm from './PurchaseForm';

const { Column } = Table;
const { Search } = Input;

const PurchaseList = () => {
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const navigate = useNavigate();
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [searchText, setSearchText] = useState('');
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [selectedPurchase, setSelectedPurchase] = useState(null);

    const fetchPurchases = async (page = 1, search = '') => {
        try {
            setLoading(true);
            const response = await purchaseService.getAllPurchases({
                page,
                limit: pagination.pageSize,
                search
            });

            setPurchases(Array.isArray(response?.data) ? response.data : []);

            setPagination(prev => ({
                ...prev,
                current: response?.pagination?.page || 1,
                total: response?.pagination?.total || 0
            }));
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch purchase invoices'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPurchases();
    }, []);

    const handleTableChange = (pagination) => {
        fetchPurchases(pagination.current, searchText);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchPurchases(1, value);
    };

    const handleAdd = () => {
        setSelectedPurchase(null);
        setIsDrawerVisible(true);
    };

    const handleEdit = (purchase) => {
        setSelectedPurchase(purchase);
        setIsDrawerVisible(true);
    };

    const handleView = (id) => {
        navigate(`/purchases/${id}`);
    };

    const handleDrawerClose = () => {
        setIsDrawerVisible(false);
        setSelectedPurchase(null);
    };

    const handleSuccess = () => {
        handleDrawerClose();
        fetchPurchases(pagination.current, searchText);
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Row justify="space-between" align="middle" gutter={[8, 8]}>
                        <Col xs={24} sm={12}>
                            <Space>
                                <Avatar size={isMobile ? "middle" : "large"} icon={<ShoppingCartOutlined />} style={{ backgroundColor: '#1890ff' }} />
                                <div>
                                    <h3 style={{ margin: 0, fontSize: isMobile ? '16px' : '20px' }}>{isMobile ? 'Purchases' : 'Purchase Invoices'}</h3>
                                    {!isMobile && <p style={{ margin: 0, color: '#8c8c8c' }}>Manage vendor purchases</p>}
                                </div>
                            </Space>
                        </Col>
                        <Col xs={24} sm={12} style={{ textAlign: isMobile ? 'left' : 'right' }}>
                            <Space wrap>
                                <Search
                                    placeholder="Search..."
                                    allowClear
                                    enterButton={<SearchOutlined />}
                                    size={isMobile ? "middle" : "large"}
                                    style={{ width: isMobile ? '160px' : '300px' }}
                                    onSearch={handleSearch}
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    size={isMobile ? "middle" : "large"}
                                    onClick={handleAdd}
                                >
                                    {isMobile ? "Add" : "New Purchase"}
                                </Button>
                            </Space>
                        </Col>
                    </Row>

                    <Table
                        dataSource={purchases}
                        loading={loading}
                        rowKey="id"
                        pagination={{
                            ...pagination,
                            size: isMobile ? 'small' : 'middle',
                            showSizeChanger: !isMobile
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 'max-content' }}
                        size={isMobile ? 'small' : 'middle'}
                    >
                        {isMobile && (
                            <Column
                                title="Purchase"
                                key="purchaseMobile"
                                render={(record) => (
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 700 }}>{record.invoiceNumber}</div>
                                        <div style={{ fontSize: '12px' }}>{record.supplier?.vendorName}</div>
                                        <div style={{ color: '#52c41a', fontWeight: 600 }}>₹{parseFloat(record.totalAfterTax || 0).toFixed(2)}</div>
                                    </div>
                                )}
                            />
                        )}
                        <Column
                            title="Invoice Number"
                            dataIndex="invoiceNumber"
                            key="invoiceNumber"
                            responsive={['md']}
                            width={140}
                            render={(text) => <strong>{text}</strong>}
                        />
                        <Column
                            title="Vendor"
                            dataIndex={['vendor', 'vendorName']}
                            key="vendorName"
                            responsive={['md']}
                            width={200}
                            ellipsis={true}
                        />
                        <Column
                            title="Date"
                            dataIndex="invoiceDate"
                            key="invoiceDate"
                            responsive={['md']}
                            width={120}
                            render={(date) => new Date(date).toLocaleDateString()}
                        />
                        <Column
                            title="Items"
                            dataIndex="items"
                            key="items"
                            responsive={['md']}
                            width={80}
                            render={(items) => items?.length || 0}
                        />
                        <Column
                            title="Total Before Tax"
                            dataIndex="totalBeforeTax"
                            key="totalBeforeTax"
                            responsive={['md']}
                            width={140}
                            render={(amount) => `₹${parseFloat(amount || 0).toFixed(2)}`}
                        />
                        <Column
                            title="Tax"
                            dataIndex="totalTax"
                            key="totalTax"
                            responsive={['md']}
                            width={120}
                            render={(amount) => `₹${parseFloat(amount || 0).toFixed(2)}`}
                        />
                        <Column
                            title="Total"
                            dataIndex="totalAfterTax"
                            key="totalAfterTax"
                            responsive={['md']}
                            width={140}
                            render={(amount) => (
                                <strong style={{ color: '#52c41a' }}>
                                    ₹{parseFloat(amount || 0).toFixed(2)}
                                </strong>
                            )}
                        />
                        <Column
                            title="Actions"
                            key="actions"
                            fixed={isMobile ? false : "right"}
                            width={isMobile ? 80 : 100}
                            render={(_, record) => (
                                <Space>
                                    <Tooltip title="View">
                                        <Button
                                            type="text"
                                            icon={<EyeOutlined style={{ color: '#52c41a' }} />}
                                            onClick={() => handleView(record.id)}
                                        />
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                        <Button
                                            type="text"
                                            icon={<EditOutlined style={{ color: '#1890ff' }} />}
                                            onClick={() => handleEdit(record)}
                                        />
                                    </Tooltip>
                                </Space>
                            )}
                        />
                    </Table>
                </Space>
            </Card>

            <Drawer
                title={selectedPurchase ? 'Edit Purchase Invoice' : 'Create Purchase Invoice'}
                width={isMobile ? '100%' : 720}
                onClose={handleDrawerClose}
                open={isDrawerVisible}
                destroyOnClose
            >
                <PurchaseForm
                    purchase={selectedPurchase}
                    onSuccess={handleSuccess}
                    onCancel={handleDrawerClose}
                />
            </Drawer>
        </Space>
    );
};

export default PurchaseList;

